{
  "app": {
    "name": "meta-mdm",
    "tagline": "Master data, defined at runtime",
    "console": "Admin console",
    "footer": "© {{year}} meta-mdm. All rights reserved."
  },
  "nav": {
    "skipToContent": "Skip to content",
    "primary": "Primary",
    "breadcrumbs": "Breadcrumb",
    "openMenu": "Open navigation",
    "closeMenu": "Close navigation",
    "group": {
      "data": "Data",
      "governance": "Governance",
      "admin": "Admin"
    },
    "breadcrumb": {
      "new": "New",
      "edit": "Edit",
      "record": "Record",
      "schema": "Schema",
      "submission": "Submission"
    },
    "dashboard": "Dashboard",
    "masterTypes": "Master types",
    "records": "Records",
    "submissions": "Submissions",
    "handovers": "Handovers",
    "audit": {
      "masterData": "Audit",
      "userManagement": "User audit"
    },
    "users": "Users",
    "account": "Account",
    "about": {
      "dashboard": "What is waiting on you, and how much master data exists.",
      "records": "Browse and edit the records themselves, one master type at a time.",
      "masterTypes": "The kinds of master data this tenant defines, and the schema each one uses.",
      "submissions": "Changes waiting on a decision, and the ones you submitted yourself.",
      "handovers": "Requests to move a submission to a different reviewer.",
      "auditMasterData": "Who changed which record, when, and what the change was.",
      "auditUserManagement": "Who provisioned, activated or granted a role to whom.",
      "users": "Provision people, grant roles, and activate or deactivate access."
    }
  },
  "auth": {
    "signIn": "Sign in",
    "signInPrompt": "Sign in to continue to meta-mdm.",
    "signInWithGoogle": "Continue with Google",
    "signInWithMicrosoft": "Continue with Microsoft",
    "signOut": "Sign out",
    "signedInAs": "Signed in as {{email}}",
    "checkingSession": "Checking your session",
    "pending": {
      "title": "Your account is waiting for approval",
      "body": "An administrator has to activate your account before you can see any master data. You do not need to do anything else — this page will work as soon as they do.",
      "action": "Check again"
    },
    "deactivated": {
      "title": "Your account has been deactivated",
      "body": "You can still sign in, but you no longer have access to master data. Contact an administrator if you think this is a mistake."
    },
    "noPermissions": {
      "title": "No access has been granted yet",
      "body": "Your account is active but holds no roles, so there is nothing to show. An administrator can grant you access to a master type."
    }
  },
  "table": {
    "search": "Search",
    "searchHint": "Press / to search",
    "loading": "Loading rows",
    "loadingMore": "Loading more rows",
    "refreshing": "Refreshing",
    "loadMore": "Load more",
    "endOfResults": "That is everything",
    "sortedAscending": "sorted ascending",
    "sortedDescending": "sorted descending",
    "notSorted": "not sorted",
    "empty": {
      "title": "Nothing here yet",
      "body": "Once records are created they will appear in this table."
    },
    "noResults": {
      "title": "No matches",
      "body": "No rows match the filters you have applied. Remove one to widen the search.",
      "clearFilters": "Clear filters",
      "removeFilter": "Remove the {{field}} filter"
    },
    "error": {
      "title": "This table could not be loaded",
      "body": "The request failed before any rows came back. Retrying usually works; if it does not, quote the trace id below.",
      "traceId": "Trace id"
    }
  },
  "diff": {
    "added": "Added",
    "removed": "Removed",
    "changed": "Changed",
    "before": "Before",
    "after": "After",
    "none": "Nothing"
  },
  "common": {
    "retry": "Try again",
    "cancel": "Cancel",
    "confirm": "Confirm",
    "close": "Close",
    "copy": "Copy",
    "copied": "Copied",
    "copyTraceId": "Copy the trace id",
    "required": "required",
    "loading": "Loading",
    "theme": {
      "label": "Theme",
      "light": "Light",
      "dark": "Dark",
      "system": "Match system"
    },
    "locale": {
      "label": "Language",
      "en": "English",
      "hi": "Hindi"
    },
    "aboutSection": "About {{name}}"
  },
  "status": {
    "draft": "Draft",
    "pending": "Pending review",
    "approved": "Approved",
    "rejected": "Rejected",
    "cancelled": "Cancelled",
    "active": "Active",
    "inactive": "Inactive",
    "uncommissioned": "Not yet activated"
  },
  "errors": {
    "generic": {
      "title": "Something did not work",
      "body": "The request could not be completed. Try again in a moment."
    },
    "offline": {
      "title": "No connection",
      "body": "Your browser could not reach the server. Check your connection and try again."
    },
    "notFound": {
      "title": "That page does not exist",
      "body": "The link may be out of date, or the record may have been removed.",
      "action": "Go to the dashboard"
    },
    "forbidden": {
      "title": "You do not have access to this",
      "body": "Your account holds no permission for this action. An administrator can grant it."
    }
  },
  "dashboard": {
    "sections": {
      "masterData_one": "Master data · {{count}} type",
      "masterData_other": "Master data · {{count}} types",
      "recentlyUpdated": "Recently updated",
      "approvals": "Approvals"
    },
    "subtitle": "Here is what is waiting on you today.",
    "greeting": {
      "morning": "Good morning, {{name}}",
      "afternoon": "Good afternoon, {{name}}",
      "evening": "Good evening, {{name}}"
    },
    "cta": {
      "reviewSubmissions": "Review submissions",
      "goToRecords": "Go to records"
    },
    "currentRecords": "Current records",
    "countUnavailable": "Count unavailable",
    "filterTypes": "Filter by name",
    "noRecentActivity": "No recent activity",
    "version": "Version {{version}}",
    "pendingApproval": "Pending approval",
    "approvals": {
      "schema": "Schema approvals",
      "record": "Record submissions"
    },
    "about": {
      "masterData": "One tile per master type you can read, with how many current records it holds.",
      "recent": "The most recently changed records across every type you can read.",
      "approvals": "How much is waiting on a decision right now."
    }
  },
  "masterTypes": {
    "columns": {
      "code": "Code",
      "name": "Name",
      "status": "Status"
    },
    "description": "The master types this tenant defines. Records are created against these."
  },
  "records": {
    "title": "Records",
    "filterTypes": "Filter by name",
    "description": "Master data, one section per type you have access to.",
    "pickType": "Choose a master type to browse its records.",
    "noTypes": {
      "title": "No master types you can read",
      "body": "Your account holds no record permission on any master type yet. An administrator can grant one."
    },
    "view": {
      "label": "View",
      "list": "List",
      "tree": "Tree",
      "about": {
        "list": "Every record in this type as one flat, sortable table.",
        "tree": "The same records arranged under their parents, loaded a level at a time."
      }
    },
    "columns": {
      "businessKey": "Key",
      "name": "Name",
      "status": "Status",
      "active": "Active",
      "version": "Version",
      "updated": "Updated"
    },
    "includeInactive": "Include inactive",
    "statusFilter": {
      "label": "Status",
      "all": "All"
    },
    "create": "New record",
    "empty": {
      "title": "No records yet",
      "body": "Nothing has been created for this master type. A record starts as a draft and becomes visible to everyone once a reviewer approves it."
    },
    "noSchema": {
      "title": "This master type has no active schema",
      "body": "Records cannot be created until a schema version has been defined and approved. Until then there is nothing to fill in.",
      "action": "Define a schema"
    },
    "draftBadge": "Your draft",
    "form": {
      "createTitle": "New {{type}}",
      "amendTitle": "Amend {{key}}",
      "editTitle": "Edit {{key}}",
      "createDescription": "Saved as a draft. It becomes visible to everyone once you submit it and a reviewer approves.",
      "amendDescription": "Creates a new version. The current one stays live until a reviewer approves this.",
      "businessKey": "Business key",
      "businessKeyHint": "The record's permanent identity. Never translated, and fixed once created.",
      "parent": "Parent {{type}}",
      "parentLocked": "The parent is fixed for an open draft. Withdraw it and start again to change the parent.",
      "fields": "{{type}} fields",
      "choose": "Choose...",
      "save": "Save draft",
      "saved": "Draft saved",
      "schemaInvalid": {
        "title": "This master type's schema cannot be rendered",
        "body": "The stored definition is malformed, so no form can be built from it. Fix it in the schema builder. Nothing typed here could be saved."
      }
    },
    "reference": {
      "placeholder": "Search...",
      "results": "Matching records",
      "noMatches": "Nothing matched",
      "refine": "Showing the first {{count}}. Keep typing to narrow it down.",
      "clear": "Clear the selection"
    },
    "detail": {
      "fields": "Fields",
      "hierarchy": "Hierarchy",
      "children": "Children",
      "noChildren": "No records point at this one.",
      "history": "Version history",
      "historyEmpty": "No versions you can see.",
      "viewAuditTrail": "View full audit trail",
      "createdBy": "Created by",
      "approvedBy": "Approved by",
      "notApproved": "Not approved",
      "version": "Version {{version}}",
      "current": "Current",
      "noValue": "None",
      "actions": "Actions",
      "edit": "Edit draft",
      "amend": "New version",
      "submit": "Submit for review",
      "submitted": "Submitted for review",
      "deactivate": "Deactivate",
      "reactivate": "Reactivate",
      "cascade": "Deactivate its descendants too",
      "confirmDeactivate": {
        "title": "Deactivate this record?",
        "body": "Deactivation is a versioned change like any other. It is submitted for review, and the record stays live until a reviewer approves it."
      }
    },
    "tree": {
      "expand": "Expand {{name}}",
      "collapse": "Collapse {{name}}",
      "empty": "No records to show.",
      "loading": "Loading children"
    },
    "cannotCreate": "You can browse these records but not add one. Creating needs the creator role on this master type.",
    "cannotCreateNoSchema": "This master type has no active schema yet, so there are no fields to fill in. Define one and have it approved first."
  },
  "schema": {
    "title": "Schema",
    "builderTitle": "Schema for {{type}}",
    "description": "The fields records of this type carry. Changing them creates a new version, which a reviewer has to approve before it takes effect.",
    "activeVersion": "Active: version {{version}}",
    "noActiveVersion": "No version has been activated yet.",
    "addField": "Add field",
    "removeField": "Remove {{name}}",
    "moveUp": "Move {{name}} up",
    "moveDown": "Move {{name}} down",
    "field": {
      "untitled": "Field {{position}}",
      "name": "Name",
      "nameHint": "Lowercase, no spaces. This is the key the value is stored under, and it cannot be renamed later.",
      "type": "Type",
      "label": "Label",
      "required": "Required",
      "localised": "Translated per language",
      "values": "Allowed values",
      "valuesHint": "One per line.",
      "target": "Points at",
      "pattern": "Pattern",
      "minLength": "Min length",
      "maxLength": "Max length",
      "minimum": "Minimum",
      "maximum": "Maximum"
    },
    "types": {
      "string": "Text",
      "number": "Number",
      "integer": "Whole number",
      "boolean": "Yes or no",
      "date": "Date",
      "datetime": "Date and time",
      "enum": "One of a list",
      "reference": "Another record"
    },
    "noFields": "No fields yet. A schema with no fields produces a form with nothing in it.",
    "saveDraft": "Save draft version",
    "submit": "Submit for review",
    "versions": "Versions",
    "compatibility": {
      "label": "Compatibility",
      "compatible": "Compatible",
      "breaking": "Breaking",
      "unknown": "Not yet classified",
      "breakingBody": "Existing records do not satisfy this version. It cannot be activated until they have been migrated."
    },
    "createType": {
      "action": "New master type",
      "title": "New master type",
      "code": "Code",
      "codeHint": "Lowercase letters, digits, dashes and underscores. Permanent.",
      "name": "Name",
      "parent": "Parent type",
      "noParent": "None. This is a top-level type",
      "create": "Create"
    },
    "cannotDefine": "You can view this schema but not change it. Defining fields needs the creator role on this master type — an administrator can grant it."
  },
  "forms": {
    "errors": {
      "required": "Enter a value.",
      "wrongType": "That is not a valid value for this field.",
      "minLength_one": "Use at least {{count}} character.",
      "minLength_other": "Use at least {{count}} characters.",
      "maxLength_one": "Use {{count}} character or fewer.",
      "maxLength_other": "Use {{count}} characters or fewer.",
      "minimum": "Enter {{value}} or more.",
      "maximum": "Enter {{value}} or less.",
      "pattern": "That does not match the format this field expects.",
      "date": "Enter a real date.",
      "datetime": "Enter a real date and time.",
      "oneOf": "Choose one of: {{values}}.",
      "localeRequired": "Fill in the {{locale}} tab. It is required.",
      "anyLocale": "Fill in at least one language."
    }
  },
  "submissions": {
    "title": "Submissions",
    "description": "Changes waiting on a decision, and what has already been decided.",
    "approve": "Approve",
    "reject": "Reject",
    "approved": "Submission approved",
    "rejected": "Submission rejected",
    "changes": "What this changes",
    "decisionComment": "Reviewer's comment",
    "backToQueue": "Back to the queue",
    "unassigned": "Unassigned",
    "notSubmitted": "Not submitted",
    "cascadeNotice": "This is one decision covering {{count}} records. Approving it applies to all of them.",
    "columns": {
      "type": "Master type",
      "change": "Change",
      "status": "Status",
      "submittedBy": "Submitted by",
      "reviewer": "Reviewer",
      "waiting": "Waiting",
      "subject": "Subject"
    },
    "kind": {
      "record": "Record",
      "schema": "Schema"
    },
    "action": {
      "create": "New record",
      "update": "Record edit",
      "deactivate": "Deactivation",
      "reactivate": "Reactivation",
      "import": "Bulk import",
      "schema_version": "Schema version"
    },
    "scope": {
      "label": "Queue",
      "mine": "Waiting on me",
      "submitted": "I submitted",
      "all": "Everything",
      "about": {
        "mine": "Assigned to you, plus everything nobody has been assigned yet.",
        "submitted": "Changes you submitted, and what has become of them.",
        "all": "Every submission in the tenant, decided or not."
      }
    },
    "filter": {
      "status": "Status",
      "kind": "Kind",
      "any": "Any"
    },
    "age": {
      "fresh_one": "{{count}} day",
      "fresh_other": "{{count}} days",
      "ageing_one": "Waiting {{count}} day",
      "ageing_other": "Waiting {{count}} days",
      "stalled_one": "Stalled {{count}} day",
      "stalled_other": "Stalled {{count}} days"
    },
    "empty": {
      "mine": {
        "title": "Nothing is waiting on you",
        "body": "Submissions routed to you appear here. An unassigned one shows up for every reviewer until somebody decides it."
      },
      "submitted": {
        "title": "You have not submitted anything",
        "body": "Changes you send for review appear here, along with the decision when one is made."
      },
      "all": {
        "title": "No submissions match",
        "body": "Nothing in this tenant matches the filters above. Widen them to see more."
      }
    },
    "review": {
      "title": "Review: {{type}}"
    },
    "item": {
      "schemaVersion": "Schema version {{version}}",
      "record": "Record",
      "depth": "Level {{depth}}"
    },
    "diff": {
      "fields": "Fields",
      "attributes": "Record attributes",
      "noChanges": "Nothing changed in this item.",
      "noItems": "This submission has no items to show."
    },
    "refusal": {
      "forbidden": "You do not hold the permission to decide this. A reviewer grant on this master type is what it needs.",
      "self-review-refused": "You cannot approve your own submission. Somebody else has to review it — that separation is the point of the workflow.",
      "not-assigned-reviewer": "Somebody else is the assigned reviewer for this submission. Ask them, or request a handover.",
      "submission-not-pending": "This submission has already been decided, so there is nothing left to approve.",
      "insufficient-review-coverage": "This change reaches master types you do not hold an approve grant on.",
      "insufficientCoverage": "Approving this needs a reviewer grant on: {{types}}."
    },
    "rejectDialog": {
      "title": "Reject this submission",
      "body": "The person who submitted it will see your comment, and it is recorded in the audit trail.",
      "comment": "Why are you rejecting it?",
      "commentHint": "Say what would have to change for this to be approved.",
      "commentRequired": "A rejection needs a comment. Say what would have to change."
    },
    "handover": {
      "request": "Request handover",
      "title": "Hand this over to another reviewer",
      "body": "An administrator has to approve the request. Until they do, this submission stays yours to decide.",
      "to": "Hand over to",
      "toHint": "Reviewers on this master type. The person who submitted it cannot be chosen.",
      "reason": "Why?",
      "requested": "Handover requested",
      "incomplete": "Choose a reviewer and give a reason first.",
      "notPending": "This submission has already been decided, so there is nothing to hand over.",
      "notYours": "Only the assigned reviewer can hand a submission over."
    },
    "itemCount_one": "{{count}} record",
    "itemCount_other": "{{count}} records",
    "decide": "Decision"
  },
  "users": {
    "title": "Users",
    "description": "Provision people, activate or deactivate their access, and grant or revoke roles.",
    "columns": {
      "email": "User",
      "status": "Status",
      "roles": "Roles",
      "actions": "Actions"
    },
    "noRoles": "No roles granted",
    "activate": "Activate",
    "activated": "User activated",
    "activateNeedsGrant": "Grant a role before activating. A user with no roles would be refused everywhere, which is worse than leaving them uncommissioned.",
    "deactivate": "Deactivate",
    "deactivated": "User deactivated",
    "deactivateBlockedDetail": "They still hold assigned pending submissions. Clear them with a reviewer handover or by changing the standing reviewer assignment, then try again.",
    "confirmDeactivate": {
      "title": "Deactivate this user?",
      "body": "{{email}} will lose access immediately — their session is revoked, not just their next sign-in."
    },
    "empty": {
      "title": "No users yet",
      "body": "Provision the first person to get started."
    },
    "roles": {
      "viewer": "Viewer",
      "creator": "Creator",
      "reviewer": "Reviewer",
      "admin": "Admin",
      "super_admin": "Super admin"
    },
    "provision": {
      "action": "Provision user",
      "title": "Provision a user",
      "description": "Creates the account, not access. They cannot sign in and do anything until you grant a role and activate them.",
      "email": "Email",
      "displayName": "Name",
      "success": "User provisioned"
    },
    "grant": {
      "action": "Grant role",
      "revoked": "Role revoked",
      "revokeAction": "Revoke {{role}} · {{scope}}",
      "title": "Grant a role",
      "description": "For {{email}}.",
      "role": "Role",
      "scope": "Master type",
      "scopeHint": "Leave as Global to grant it everywhere, or scope it to one master type.",
      "global": "Global",
      "alwaysGlobal": "Admin and super admin are always global — there is no concept of \"admin of one master type\" in this system.",
      "noScopeVisibility": "Your account cannot browse master types, so a scoped grant cannot be offered here. This grants globally instead — ask someone who holds master-type read access if you need it scoped to just one.",
      "success": "Role granted",
      "confirmRevoke": {
        "title": "Revoke this role?",
        "body": "Removes {{role}} · {{scope}} from {{email}}. If it is their last role, they return to not-yet-activated and lose access immediately."
      }
    }
  },
  "handovers": {
    "title": "Handovers",
    "description": "Requests to move a submission to a different reviewer.",
    "summary": "{{from}} asked to hand this over to {{to}}.",
    "viewSubmission": "View the submission",
    "approve": "Approve handover",
    "reject": "Reject handover",
    "approved": "Handover approved",
    "rejected": "Handover rejected",
    "empty": {
      "title": "No open handovers",
      "body": "When a reviewer asks to pass a submission to somebody else, the request appears here for an administrator to decide."
    }
  },
  "audit": {
    "masterData": {
      "title": "Audit",
      "description": "Every change to master data and schema, who made it, and who approved it."
    },
    "userManagement": {
      "title": "User audit",
      "description": "User provisioning, activation, and role grants — visible to administrators only."
    },
    "domain": {
      "label": "Domain",
      "masterData": "Master data",
      "schema": "Schema",
      "userManagement": "User management"
    },
    "columns": {
      "occurredAt": "When",
      "action": "Action",
      "entity": "Entity",
      "actor": "Actor"
    },
    "filter": {
      "entityType": "Entity type",
      "entity": "Entity",
      "action": "Action",
      "actor": "Actor",
      "any": "Any",
      "from": "From",
      "to": "To"
    },
    "action": {
      "created": "Created",
      "updated": "Updated",
      "submitted": "Submitted",
      "approved": "Approved",
      "rejected": "Rejected",
      "deactivated": "Deactivated",
      "reactivated": "Reactivated",
      "activated": "Activated",
      "role_granted": "Role granted",
      "role_revoked": "Role revoked",
      "identity_bound": "Sign-in linked",
      "imported": "Bulk import",
      "handover_requested": "Handover requested",
      "handover_decided": "Handover decided"
    },
    "diff": {
      "changes": "What changed",
      "before": "Before",
      "after": "After",
      "none": "Nothing was recorded for this change."
    },
    "import": {
      "title_one": "Bulk import: {{count}} record",
      "title_other": "Bulk import: {{count}} records",
      "badge_one": "{{count}} record",
      "badge_other": "{{count}} records"
    },
    "export": {
      "action": "Export",
      "working": "Exporting…",
      "csv": "Export as CSV",
      "xlsx": "Export as XLSX",
      "started": "Export started. It will download automatically once it is ready.",
      "failed": "The export failed",
      "pollFailed": "Lost track of the export's progress",
      "downloadFailed": "The file could not be downloaded. Try exporting again."
    },
    "empty": {
      "title": "No audit activity yet",
      "body": "Once something changes, it will appear here."
    }
  },
  "bulk": {
    "format": {
      "csv": "CSV",
      "xlsx": "Excel (XLSX)"
    },
    "template": {
      "action": "Download template"
    },
    "import": {
      "choose": "Choose file",
      "action": "Upload",
      "started": "Import started",
      "progress": "{{imported}} imported, {{failed}} failed, of {{total}} rows",
      "errorReport": "Download error report"
    },
    "export": {
      "action": "Export",
      "started": "Export started",
      "ready": "Export ready",
      "download": "Download export",
      "rows": "{{count}} rows"
    },
    "status": {
      "pending": "Queued",
      "running": "Running",
      "succeeded": "Completed",
      "partial": "Completed with errors",
      "failed": "Failed"
    }
  },
  "tooltips": {
    "approve": "Publishes this change to everyone who can read the type. There is no undo.",
    "reject": "Sends it back with your reason. The creator can fix it and submit again.",
    "requestHandover": "Asks an administrator to move this to another reviewer. It stays yours to decide until they agree.",
    "saveDraft": "Keeps this to yourself. Nobody else sees it until you submit it for review.",
    "submitForReview": "Hands this to a reviewer. You cannot edit it while it waits.",
    "newRecord": "Starts a draft. It becomes visible to everyone once a reviewer approves it.",
    "createType": "Defines a new kind of master data. It needs a schema before any record can use it.",
    "provisionUser": "Creates the account only. They still need a role and activation before they can do anything.",
    "grantRole": "Adds a role, either everywhere or scoped to a single master type.",
    "activateUser": "Gives them access straight away, with whatever roles they hold now.",
    "deactivateUser": "Revokes their session immediately, not just their next sign-in.",
    "approveHandover": "Moves the submission to the new reviewer. Only now does the original one lose it.",
    "rejectHandover": "Leaves the submission with the reviewer who has it.",
    "reviewSubmissions": "Opens the queue of changes waiting on a decision.",
    "goToRecords": "Opens the master data itself, grouped by type.",
    "signInGoogle": "Sends you to Google to sign in. You come back here afterwards.",
    "signInMicrosoft": "Sends you to Microsoft Entra to sign in. You come back here afterwards.",
    "notFoundHome": "Goes back to the dashboard.",
    "account": "Who you are signed in as, and the way out.",
    "checkAgain": "Asks the server again whether an administrator has activated you yet.",
    "cancel": "Closes this without saving. Nothing you have typed is kept.",
    "loadMore": "Fetches the next page. Rows already on screen stay where they are.",
    "clearFilters": "Removes every filter and shows the whole list again.",
    "retry": "Runs the same request again.",
    "editRecord": "Opens the draft for editing. Nobody else sees it until you submit it.",
    "amendRecord": "Starts a new version. The current one stays live until a reviewer approves the change.",
    "deactivateRecord": "Retires this record. A reviewer has to approve that before readers stop seeing it.",
    "reactivateRecord": "Brings this record back into use, once a reviewer approves it.",
    "cascade": "Also retires every record beneath this one, as a single submission a reviewer decides once.",
    "includeInactive": "Also lists records that have been retired. They stay read-only.",
    "template": "Downloads a spreadsheet with this type's columns, ready to fill in.",
    "chooseFile": "Picks a CSV or XLSX file from your computer. Nothing uploads until you press Import.",
    "import": "Uploads the file and checks every row. The valid rows become one submission; the rest are reported back.",
    "cancelUpload": "Forgets the chosen file. Nothing has been uploaded yet.",
    "export": "Builds a file of these records. A large one runs in the background and appears here when it is ready.",
    "downloadExport": "Downloads the finished file. The link is short-lived and minted fresh each time.",
    "importErrors": "Lists the rows that were refused, and why each one was.",
    "auditExport": "Builds a file of this audit trail. It runs in the background and appears here when it is ready.",
    "submitSchema": "Sends this version for review. It becomes the active schema once a reviewer approves it.",
    "parentType": "Makes records of this type children of another. Leave it unset for a top-level type.",
    "fieldType": "Decides what a value may hold, and which control the form renders for it.",
    "fieldTarget": "Which master type this reference points at.",
    "fieldRequired": "A record cannot be saved without a value here.",
    "fieldLocalised": "Stores one value per locale, so this field can be translated.",
    "grantRoleSubmit": "Grants the role now. It applies from their next request.",
    "grantRoleRole": "What they may do. Admin and super admin are always global.",
    "grantRoleScope": "Leave as Global to grant it everywhere, or pick one master type to limit it to.",
    "provisionSubmit": "Creates the account. They still need a role and activation before they can do anything.",
    "statusFilter": "Narrows the list to one status. It filters what you see, not what exists.",
    "kindFilter": "Narrows the list to record changes or to schema changes.",
    "handoverTo": "Who takes over the decision. The person who submitted it cannot be chosen.",
    "localeTab": "The values stored for {{language}}."
  },
  "pageHelp": {
    "submissions": "The queue is chosen server-side, not by you — the selected tab is the scope it decided, and \"Waiting on me\" includes everything nobody has been assigned.",
    "records": "Only the master types you hold a read grant on appear here. A type with no active schema cannot take records at all.",
    "masterTypes": "A type needs an active schema before any record can use it. Opening a row goes to that schema, not to its records.",
    "users": "Provisioning, granting a role and activating are three separate steps, in that order. An account with no role is refused everywhere, which is why it cannot be activated without one.",
    "handovers": "Only an administrator can decide one. Until they do, the submission stays with the reviewer who has it — nothing is frozen while a request is open."
  }
}
